import { Button, Card, Col, Form, Input, Result, Row, Typography } from "antd";
import axios from "axios";
import { message } from "@bindeleddet/core/utils";
import { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { BACKEND_PASSWORD_ERROR, PASSWORD_RULE } from "helpers/password";

const { Title } = Typography;

interface RouteParams {
  token: string;
}

const StudentResetPassword = () => {
  const { token } = useParams<RouteParams>();
  const history = useHistory();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [invalidToken, setInvalidToken] = useState(false);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const values = form.getFieldsValue();
      await axios.post(`/students/reset-password/${token}/`, {
        new_password: values.new_password,
        confirm_password: values.confirm_password,
      });
      message("success", "Passordet er oppdatert");
      history.push("/"); // back to login page
    } catch (error: unknown) {
      const responseData = (error as { response?: { data?: Record<string, unknown> } }).response?.data;
      const detail = typeof responseData?.error === "string" ? responseData.error : undefined;
      if (detail?.toLowerCase().includes("invalid token")) {
        setInvalidToken(true);
        return;
      }
      const newPasswordErrors =
        Array.isArray(responseData?.new_password) &&
        (responseData?.new_password || []).every(item => typeof item === "string")
          ? (responseData?.new_password as string[]) ?? []
          : [];
      if (typeof newPasswordErrors[0] === "string" && newPasswordErrors[0].includes(BACKEND_PASSWORD_ERROR)) {
        return message("error", PASSWORD_RULE.message);
      }
      if (detail?.toLowerCase().includes("passwords do not match")) {
        return message("error", '"Nytt passord" og "Bekreft passord" samsvarer ikke');
      }
      message("error", "Kunne ikke tilbakestille passord");
    } finally {
      setSubmitting(false);
    }
  };

  if (invalidToken) {
    return (
      <Row justify="center" style={{ padding: "48px 16px" }}>
        <Col xs={24} sm={20} md={12} lg={10}>
          <Result
            status="error"
            title="Ugyldig lenke"
            subTitle="Lenken for tilbakestilling er ikke gyldig eller har utløpt. Be om en ny lenke og prøv igjen."
          />
        </Col>
      </Row>
    );
  }

  return (
    <Row justify="center" style={{ padding: "48px 16px" }}>
      <Col xs={24} sm={20} md={12} lg={10}>
        <Card>
          <Title level={3} style={{ textAlign: "center", marginBottom: "24px" }}>
            Tilbakestill passord
          </Title>
          <Form form={form} layout="vertical" requiredMark={false} onFinish={handleSubmit}>
            <Form.Item
              label="Nytt passord"
              name="new_password"
              rules={[{ required: true, message: "Vennligst skriv inn nytt passord" }, PASSWORD_RULE]}
              hasFeedback
            >
              <Input.Password placeholder="Nytt passord" />
            </Form.Item>
            <Form.Item
              label="Bekreft nytt passord"
              name="confirm_password"
              dependencies={["new_password"]}
              rules={[
                { required: true, message: "Vennligst bekreft nytt passord" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("new_password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("De to passordene samsvarer ikke"));
                  },
                }),
              ]}
              hasFeedback
            >
              <Input.Password placeholder="Bekreft nytt passord" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" block loading={submitting}>
                Bekreft
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </Row>
  );
};

export default StudentResetPassword;
